import { getAuthValue, AUTH_STORAGE_KEYS } from "./authApi";

const API_BASE = process.env.REACT_APP_API_SERVER;

export class UnauthorizedError extends Error {
  constructor(status) {
    super(`Unauthorized (${status})`);
    this.name = "UnauthorizedError";
    this.status = status;
  }
}

function sanitize3cxDetail(detail) {
  if (!detail || typeof detail !== "string") return "";
  return detail.replace(/^Invalid parameter 'UpgradeKey': .*?\.\s*/i, "");
}

async function request(path, { method = "GET", body } = {}) {
  const accessToken = getAuthValue(AUTH_STORAGE_KEYS.accessToken);
  const headers = {
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(body ? { "Content-Type": "application/json" } : {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 || res.status === 403) {
    throw new UnauthorizedError(res.status);
  }

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const detail = sanitize3cxDetail(data?.details?.detail);
    throw new Error(
      detail || data?.message || data?.error || `Request failed (${res.status})`,
    );
  }

  return data;
}

function withPartnerId(path, partnerId) {
  if (partnerId == null || String(partnerId).trim() === "") return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}partnerId=${encodeURIComponent(String(partnerId))}`;
}

export async function fetchExpiringKeys(partnerId) {
  const data = await request(withPartnerId("/wp-json/threecx/v1/expiring-keys", partnerId));
  return data.data || [];
}

export async function fetchPartners(partnerId) {
  const data = await request(withPartnerId("/wp-json/threecx/v1/partners", partnerId));
  return data?.data || data || [];
}

export async function fetchKeyInfo(licenseKey) {
  const data = await request("/wp-json/threecx/v1/key-info", {
    method: "POST",
    body: { licenseKey },
  });
  return data?.data && typeof data.data === "object" ? data.data : data;
}

export async function fetchRenewPrice(payload) {
  const data = await request("/wp-json/threecx/v1/renew-price", {
    method: "POST",
    body: payload,
  });
  return data?.data && typeof data.data === "object" ? data.data : data;
}

export async function createOrder(payload) {
  return request("/wp-json/threecx/v1/create-order", {
    method: "POST",
    body: payload,
  });
}
